import * as React from 'react'
import Helmet from 'react-helmet'

import { SiteMetadataMarketing } from '../../hooks/useSiteMetadata'
import { ErrorBoundary } from '../ErrorBoundary'
import { loadBingAds } from './bingAds'
import { loadIubenda } from './iubenda'
import { loadThirdPartyScripts } from './loadThirdPartyScripts'
import { loadOsano } from './osano'

interface ThirdPartyProps {
  marketingMetaData: SiteMetadataMarketing
  anonymousId?: string
  withMarketing?: boolean
}

let loaded: boolean = false
const ThirdPartyComponent: React.FC<ThirdPartyProps> = ({
  marketingMetaData,
  anonymousId = '',
  withMarketing = true,
}) => {
  React.useEffect(() => {
    // Avoid loading scripts twice on the same page to avoid unexpexted side effects
    if (loaded || !withMarketing) {
      return
    }

    const loadScripts = () => {
      // See backupTimeoutForScriptLoading above
      loaded = true
      loadThirdPartyScripts(marketingMetaData, anonymousId)
    }

    if (document.readyState === 'loading') {
      // Loading hasn't finished yet
      document.addEventListener('DOMContentLoaded', loadScripts)
    } else {
      // `DOMContentLoaded` has already fired
      loadScripts()
    }

    // Clean up the timeout if the load event fires
    return () => {
      document.removeEventListener('DOMContentLoaded', loadScripts)
    }
  }, [])

  React.useEffect(() => {
    loadIubenda()
    loadOsano()
    loadBingAds()
  }, [])

  return (
    <Helmet>
      <link rel="preconnect" href="https://cdn.iubenda.com" crossOrigin="" />
      <link rel="preconnect" href="https://hits-i.iubenda.com" crossOrigin="" />
      <script async type="application/ld+json">
        {`
            {
              "@context": "https://schema.org/",
              "@type": "Organization",
              "@id": "#Organization",
              "url": "https://airfocus.com/",
              "name": "airfocus",
              "description": "Home for products and the people who build them.",
              "image": "https://i.imgur.com/RFXZ7wv.png",
              "logo": "https://i.imgur.com/RFXZ7wv.png",
              "sameAs": [
                "https://www.facebook.com/airfocus/",
                "https://www.youtube.com/channel/UCRFwwMHydrKAY5ky2wdX6SA",
                "https://www.instagram.com/airfocusapp/",
                "https://www.linkedin.com/company/airfocus",
                "https://twitter.com/airfocus"
              ]
            }
          `}
      </script>
    </Helmet>
  )
}

export const ThirdParty: typeof ThirdPartyComponent = (props) => {
  return (
    <ErrorBoundary>
      <ThirdPartyComponent {...props} />
    </ErrorBoundary>
  )
}
